import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {Button} from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Typography} from '@/components/ui/typography';

import {useEmployeeWorkTimeTrackingForced} from '../hooks/useEmployeeWorkTimeTrackingForced';
import {useFinishWork} from '../hooks/useFinishWork';
import WorkTimeSignOutCountdown from './WorkTimeSignOutCountdown';

type FinishWorkDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function FinishWorkDialog({open, onOpenChange}: FinishWorkDialogProps) {
  const {t} = useTranslation();
  const isForced = useEmployeeWorkTimeTrackingForced();
  const {finishWork, isPending} = useFinishWork();

  const handleFinish = useCallback(
    async (signOutAfterFinish: boolean) => {
      await finishWork(signOutAfterFinish);

      if (!signOutAfterFinish) {
        onOpenChange(false);
      }
    },
    [finishWork, onOpenChange]
  );

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!isPending) onOpenChange(nextOpen);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('Finish work')}</DialogTitle>
        </DialogHeader>
        <DialogSection>
          {isForced && (
            <WorkTimeSignOutCountdown
              onComplete={() => void handleFinish(true)}
            />
          )}
          {!isForced && (
            <Typography>
              {t(
                'Do you want to sign out after finishing work, or stay signed in?'
              )}
            </Typography>
          )}
        </DialogSection>
        <DialogFooter>
          <Button
            type="button"
            variant="warning"
            className="sm:mr-auto"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            {t('Cancel')}
          </Button>
          {!isForced && (
            <Button
              type="button"
              variant="secondary"
              disabled={isPending}
              onClick={() => void handleFinish(false)}
            >
              {t('Finish work and stay signed in')}
            </Button>
          )}
          <Button
            type="button"
            variant="primary"
            disabled={isPending}
            onClick={() => void handleFinish(true)}
          >
            {t('Finish work and sign out')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default FinishWorkDialog;
